import { CheckIcon, GitPullRequestIcon, GoalIcon, LinkExternalIcon, XIcon } from "@primer/octicons-react";
import { rp } from "../api/client";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAction, useChange, useChanges, useWhoami } from "../api/hooks";
import { ACTIVE_STATES, canIn, openCount, type Change, type ChangeState } from "../api/types";
import { ChangeStatus, TurnChip } from "../components/ChangeStatus";
import { Avatar, Empty, Query, TimeAgo } from "../components/common";
import { MiniFlow } from "../components/Pipeline";
import { Subhead } from "../components/SettingsLayout";
import { StateIcon, StateLabel, TierLabel, stateInfo } from "../components/StateLabel";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Kbd } from "../components/ui/kbd";
import { List, ListItem, ListItemMeta, ListItemSeparator, ListItemTitle } from "../components/ui/list";
import { Select } from "../components/ui/select";
import { FilterTabs } from "../components/ui/toggle-group";
import { inboxSection, laterAttempt, latestRun, turnOf } from "../lib/supervise";
import { cn } from "../lib/utils";

const FILTERS: ChangeState[] = ["ci", "review", "ready", "train", "landing", "conflict", "landed", "verified", "reverted", "rejected", "cancelled"];
const DAY = 86_400_000;
const TIER_RANK: Record<string, number> = { high: 0, normal: 1, low: 2 };

/** Ids whose state changed in the last poll, kept for a few seconds so the row can flash. */
function useFlash(changes: Change[] | undefined) {
  const prev = useRef<Map<string, ChangeState> | null>(null);
  const [flash, setFlash] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!changes) return;
    const next = new Map(changes.map((c) => [c.id, c.state]));
    if (prev.current) {
      const moved = changes.filter((c) => prev.current!.has(c.id) && prev.current!.get(c.id) !== c.state).map((c) => c.id);
      if (moved.length) {
        setFlash(new Set(moved));
        const t = setTimeout(() => setFlash(new Set()), 4000);
        prev.current = next;
        return () => clearTimeout(t);
      }
    }
    prev.current = next;
  }, [changes]);
  return flash;
}

/**
 * The supervisor's inbox: open changes split into what needs a person or the agent and what is
 * moving on its own, plus what closed today. j/k move, Enter opens, Escape closes the preview.
 */
export function Changes() {
  const { repo = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const state = params.get("state") ?? "open";
  const q = useChanges(repo, state);
  const closed = useChanges(repo, "closed", state === "open");
  const counts = q.data?.counts ?? {};
  const openN = openCount(counts);
  const closedN = Object.values(counts).reduce((n, c) => n + c, 0) - openN;
  const flash = useFlash(q.data?.changes);
  const [selected, setSelected] = useState<string | null>(null);

  const today = (closed.data?.changes ?? []).filter((c) => Date.now() - c.updated < DAY);
  const all = q.data?.changes ?? [];
  const pool = [...all, ...today];
  const section = (c: Change) => (laterAttempt(c, pool) ? "agents" : inboxSection(c));
  // Riskiest first, then the longest waiting: what a person should decide next is on top.
  const byRisk = (a: Change, b: Change) => (TIER_RANK[a.risk_tier] ?? 3) - (TIER_RANK[b.risk_tier] ?? 3) || a.updated - b.updated;
  const sections: Array<{ key: string; title: string; hint: string; list: Change[] }> =
    state === "open"
      ? [
          { key: "you", title: "Needs you", hint: "", list: all.filter((c) => section(c) === "you").sort(byRisk) },
          { key: "agents", title: "Working", hint: "", list: all.filter((c) => section(c) === "agents") },
          { key: "today", title: "Today", hint: "", list: today },
        ]
      : [{ key: "all", title: "", hint: "", list: all }];
  const order = sections.flatMap((s) => s.list.map((c) => c.id));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest("input, textarea, select, [contenteditable], [role=dialog]") || e.metaKey || e.ctrlKey || e.altKey) return;
      const i = selected ? order.indexOf(selected) : -1;
      if (e.key === "j" || e.key === "ArrowDown") {
        setSelected(order[Math.min(order.length - 1, i + 1)] ?? null);
        e.preventDefault();
      } else if (e.key === "k" || e.key === "ArrowUp") {
        setSelected(order[Math.max(0, i - 1)] ?? null);
        e.preventDefault();
      } else if ((e.key === "Enter" || e.key === "o") && selected) {
        navigate(`${rp(repo)}/changes/${selected}`);
      } else if (e.key === "Escape") {
        setSelected(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [order, selected, navigate, repo]);

  useEffect(() => {
    if (selected) document.getElementById(`row-${selected}`)?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  const kbdHint = (
    <span className="flex items-center gap-1 text-xs text-muted-foreground max-md:hidden">
      <Kbd>j</Kbd>
      <Kbd>k</Kbd> to move, <Kbd>↵</Kbd> to open
    </span>
  );

  return (
    <>
      <Subhead title="Changes" actions={kbdHint}>
        {FILTERS.includes(state as ChangeState)
          ? all.length
            ? `${all.length} ${stateInfo(state as ChangeState).label.toLowerCase()}`
            : "No changes match this filter."
          : openN
            ? `${openN} open · ${sections.find((s) => s.key === "you")?.list.length ?? 0} need you`
            : state === "closed"
              ? closedN
                ? `${closedN} closed`
                : "No closed changes yet"
              : "No open changes"}
        {state === "open" && today.length > 0 && ` · ${today.length} closed today`}
      </Subhead>
      <div className={cn("grid gap-4", selected && "xl:grid-cols-[minmax(0,1fr)_420px]")}>
      <Card className="min-w-0">
        <div className="flex items-center gap-2 border-b border-border-muted px-4 py-2">
          <FilterTabs
            value={state}
            onValueChange={(v) => setParams({ state: v })}
            items={[
              { value: "open", icon: <GitPullRequestIcon />, label: `${openN} Open` },
              { value: "closed", icon: <CheckIcon />, label: `${closedN} Closed` },
            ]}
          />
          <Select
            size="sm"
            align="end"
            className="ml-auto w-auto"
            prefix="State:"
            value={FILTERS.includes(state as ChangeState) ? state : ""}
            onValueChange={(v) => setParams({ state: v || "open" })}
            options={[{ value: "", label: "Any" }, ...FILTERS.map((s) => ({ value: s, label: stateInfo(s).label, count: counts[s] || undefined }))]}
            aria-label="Filter by state"
          />
        </div>
        <Query q={q}>
          {() =>
            sections.every((s) => !s.list.length) ? (
              <Empty bee="idle">No changes match this filter.</Empty>
            ) : (
              sections.map((s) =>
                s.list.length || s.key === "you" ? (
                  <section key={s.key} aria-label={s.title || "Changes"}>
                    {s.title && (
                      <h3 className="m-0 flex items-baseline gap-2 border-b border-border-muted bg-muted px-4 py-1.5 text-xs font-semibold">
                        {s.title} <span className="font-normal text-muted-foreground">{s.list.length}</span>
                        <span className="font-normal text-faint max-md:hidden">{s.hint}</span>
                      </h3>
                    )}
                    {s.list.length ? (
                      <List>
                        {s.list.map((c) => (
                          <ChangeRow key={c.id} repo={repo} c={c} retriedAs={laterAttempt(c, pool)} selected={selected === c.id} flash={flash.has(c.id)} onSelect={() => setSelected(selected === c.id ? null : c.id)} decide={s.key === "you"} />
                        ))}
                      </List>
                    ) : (
                      <p className="m-0 px-4 py-3 text-muted-foreground">Nothing needs you.</p>
                    )}
                  </section>
                ) : null,
              )
            )
          }
        </Query>
      </Card>
      {selected && <Preview repo={repo} id={selected} onClose={() => setSelected(null)} />}
      </div>
    </>
  );
}

/** The change's status and key facts beside the list, without leaving it. */
function Preview({ repo, id, onClose }: { repo: string; id: string; onClose: () => void }) {
  const q = useChange(repo, id);
  const me = useWhoami();
  return (
    <aside className="sticky top-4 self-start max-lg:fixed max-lg:inset-x-2 max-lg:bottom-2 max-lg:top-auto max-lg:z-20 max-lg:max-h-[70vh] max-lg:overflow-auto">
      <Card className="mt-0 shadow-overlay">
        <Query q={q}>
          {({ change: c, evidence, audit }) => {
            const canReview = c.state === "review" && !!me.data && (!!me.data.user || me.data.kind === "admin") && me.data.sub !== c.agent_id && canIn(me.data, "write", repo);
            return (
              <div className="p-4">
                <div className="flex items-start gap-2">
                  <h2 className="m-0 min-w-0 flex-1 text-base font-semibold wrap-break-word">
                    {c.message.split("\n", 1)[0]} <span className="font-normal text-muted-foreground">#{c.id.slice(-8)}</span>
                  </h2>
                  <Button size="icon-sm" variant="ghost" onClick={onClose} aria-label="Close preview">
                    <XIcon />
                  </Button>
                </div>
                <ChangeStatus repo={repo} c={c} run={latestRun(evidence)} audit={audit} canReview={canReview} />
                <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                  <Fact k="Agent">
                    <span className="flex items-center gap-1">
                      <Avatar name={c.agent_id} size={16} agent={c.actor_kind === "agent"} /> {c.agent_id}
                      {c.assisted_by && <span className="text-muted-foreground">via {c.assisted_by}</span>}
                    </span>
                  </Fact>
                  {c.task_id && <Fact k="Task">{c.task_id}</Fact>}
                  <Fact k="Risk">
                    <TierLabel tier={c.risk_tier} />
                  </Fact>
                  <Fact k="Files">
                    <span className="flex flex-col font-mono">
                      {c.paths.slice(0, 6).map((p) => (
                        <span key={p} className="truncate" title={p}>
                          {p}
                        </span>
                      ))}
                      {c.paths.length > 6 && <span className="text-muted-foreground">+{c.paths.length - 6} more</span>}
                    </span>
                  </Fact>
                </dl>
                <Button asChild size="sm" variant="primary" className="mt-4 w-full">
                  <Link to={`${rp(repo)}/changes/${c.id}`}>
                    <LinkExternalIcon /> Open change
                  </Link>
                </Button>
              </div>
            );
          }}
        </Query>
      </Card>
    </aside>
  );
}

function Fact({ k, children }: { k: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="m-0 min-w-0">{children}</dd>
    </>
  );
}

/** The docs with checks a change edits (reason.doc_review): a spec edit decides what "done" means for its intent. */
const specsOf = (c: Change): string[] => (Array.isArray(c.reason?.doc_review) ? c.reason.doc_review : []);
const INTENT_ID = /^in_[0-9a-z]{6,12}$/;

export function ChangeRow({ repo, c, retriedAs, selected, flash, onSelect, decide }: { repo: string; c: Change; retriedAs?: Change; selected?: boolean; flash?: boolean; onSelect?: () => void; decide?: boolean }) {
  const turn = retriedAs ? "done" : turnOf(c);
  const spec = specsOf(c).length > 0;
  return (
    <ListItem
      id={`row-${c.id}`}
      className={cn("items-start transition-colors duration-700", onSelect && "cursor-pointer", selected && "bg-accent shadow-[inset_2px_0_0_var(--color-info-emphasis)]", flash && "bg-warning-subtle")}
      onClick={onSelect ? (e) => !(e.target as HTMLElement).closest("a, button") && onSelect() : undefined}
      aria-current={selected ? "true" : undefined}
    >
      <span className="mt-0.5 shrink-0">
        <StateIcon state={c.state} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <ListItemTitle asChild className="min-w-0 break-words">
            <Link to={`${rp(repo)}/changes/${c.id}`}>{c.subject ?? c.message.split("\n", 1)[0]}</Link>
          </ListItemTitle>
          <StateLabel state={c.state} />
          {spec && <Badge tone="yellow">Spec</Badge>}
          {c.risk_tier !== "normal" && <TierLabel tier={c.risk_tier} />}
          {/* In "Needs you" the section already says whose turn it is. */}
          {!decide && (turn === "you" || turn === "agent" || turn === "admin" || turn === "main") && <TurnChip turn={turn} />}
          {retriedAs && (
            <Link to={`${rp(repo)}/changes/${retriedAs.id}`} className="text-xs text-muted-foreground hover:text-link">
              retried as <span className="font-mono">#{retriedAs.id.slice(-8)}</span>
            </Link>
          )}
        </div>
        <ListItemMeta>
          <span className="font-mono">#{c.id.slice(-8)}</span>
          <ListItemSeparator />
          <span className="inline-flex min-w-0 items-center gap-1">
            opened <TimeAgo ms={c.created} /> by <Avatar name={c.agent_id} size={16} agent={c.actor_kind === "agent"} />
            <span className="truncate" title={c.agent_id}>{c.agent_id}</span>
          </span>
          {c.task_id && (
            <>
              <ListItemSeparator />
              {INTENT_ID.test(c.task_id) ? (
                <Badge asChild tone="blue" title="The intent this change works on">
                  <Link to={`${rp(repo)}/intents/${c.task_id}`}>
                    <GoalIcon size={12} /> {c.task_id}
                  </Link>
                </Badge>
              ) : (
                <Badge className="max-w-48 truncate" title={c.task_id}>{c.task_id}</Badge>
              )}
            </>
          )}
          <ListItemSeparator />
          <span>
            {c.paths.length} file{c.paths.length === 1 ? "" : "s"}
          </span>
        </ListItemMeta>
      </div>
      {decide && c.state === "review" && <Decide repo={repo} c={c} spec={spec} />}
      <div className="flex shrink-0 flex-col items-end gap-1.5 max-md:hidden">
        {ACTIVE_STATES.includes(c.state) || c.state === "conflict" ? <MiniFlow state={c.state} /> : null}
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          updated <TimeAgo ms={c.updated} />
        </span>
      </div>
    </ListItem>
  );
}

/** Approve in the row, so a person decides without leaving the list; rejecting needs a reason, on the change page. */
function Decide({ repo, c, spec }: { repo: string; c: Change; spec: boolean }) {
  const me = useWhoami();
  const act = useAction(repo);
  const person = !!me.data && (!!me.data.user || me.data.kind === "admin");
  if (!person || !canIn(me.data, "write", repo)) return null;
  if (me.data?.sub === c.agent_id) return <span className="shrink-0 self-center text-xs text-muted-foreground">Another person approves</span>;
  return (
    <div className="flex shrink-0 flex-col items-end gap-1 self-center">
      <Button size="sm" variant="primary" disabled={act.isPending} onClick={() => act.mutate({ path: `/v1/repos/${repo}/changes/${c.id}/approve`, body: {} })}>
        <CheckIcon /> {spec ? "Approve spec" : "Approve"}
      </Button>
      {act.error ? <span className="max-w-60 text-xs text-danger">{act.error instanceof Error ? act.error.message : "Could not approve"}</span> : null}
    </div>
  );
}
